import { Anchor, Badge, Card, Group, Stack, Text } from '@mantine/core';
import { useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSwipeGesture, SWIPE_THRESHOLD } from '../hooks/useSwipeGesture.js';
import { buildMapsUrl, type CommuteByTarget, type ObjectView } from '../lib/api.js';
import { FavoriteHeart } from './FavoriteHeart.js';
import { fmtFound } from './ListingCard.js';
import { MiniMap } from './MiniMap.js';
import { ScoreBadge } from './ScoreBadge.js';

// Höhe von Bild UND Mini-Karte (identisch, damit sie nebeneinander bündig sind).
// Viewport-relativ → schrumpft auf niedrigen Handy-Screens automatisch.
const MEDIA_H = 'clamp(130px, 20vh, 200px)';

interface Props {
  object: ObjectView;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}

function fmtPrice(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtArea(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${Number.isInteger(n) ? n : n.toFixed(1)} m²`;
}

type Tone = 'good' | 'bad' | 'neutral';
function scoreTone(score: number | null): Tone {
  if (score === null) return 'neutral';
  if (score >= 0.7) return 'good';
  if (score <= 0.4) return 'bad';
  return 'neutral';
}

/** Hintergrund-Farbe der Fahrzeit-Felder nach Minuten (näher = grüner). */
function commuteToneByMin(mins: number | null): Tone {
  if (mins === null) return 'neutral';
  if (mins <= 30) return 'good';
  if (mins <= 50) return 'neutral';
  return 'bad';
}

/** Kürzere Verbindung (Auto vs. ÖV) je Ziel + passendes Icon. */
function bestCommute(c: CommuteByTarget): { mins: number | null; icon: string } {
  const d = c.driving?.minutes ?? null;
  const t = c.transit?.minutes ?? null;
  if (d !== null && (t === null || d <= t)) return { mins: d, icon: '🚗' };
  if (t !== null) return { mins: t, icon: '🚆' };
  return { mins: null, icon: '' };
}

export function SwipeCard({ object, onSwipeLeft, onSwipeRight }: Props): JSX.Element {
  const navigate = useNavigate();
  const listingUrl = object.listing?.url ?? null;
  const [imgError, setImgError] = useState(false);
  const imageUrl = object.listing?.imageUrl ?? null;
  const hasImage = !!imageUrl && !imgError;
  const hasCoords = object.lat !== null && object.lng !== null;

  // Antippen ODER nach oben wischen (Super-Swipe) → IMMER zum Inserat. Beim Hochwischen
  // bleibt die Immobilie in der Swipe-Queue (kein Status-Wechsel). Fallback Detailseite,
  // falls keine Inserats-URL vorhanden.
  const openListing = (): void => {
    if (listingUrl) window.open(listingUrl, '_blank', 'noopener,noreferrer');
    else navigate(`/objects/${object.id}`);
  };
  const swipe = useSwipeGesture({
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp: openListing,
    onTap: openListing,
  });
  const { dragX, dragY, isDragging, handlers, rootRef, style } = swipe;

  const dragRatio = Math.min(1, Math.abs(dragX) / SWIPE_THRESHOLD);
  const upRatio = Math.min(1, Math.abs(dragY) / SWIPE_THRESHOLD);
  const isPullUp = dragY < 0 && Math.abs(dragY) > Math.abs(dragX);
  const isPullLeft = dragX < 0 && !isPullUp;
  const isPullRight = dragX > 0 && !isPullUp;

  const profileBadgeColor = object.assignedProfile === 'B_buero' ? 'cyan' : 'violet';
  const killed = (object.score?.killedBy ?? null) !== null;
  const isKauf = object.dealType === 'kauf' || object.assignedProfile === 'kauf';

  // Überschrift „Haus/Wohnung/Grundstück in <Ort>"
  const objTyp =
    object.objektTyp === 'haus'
      ? 'Haus'
      : object.objektTyp === 'grundstueck'
        ? 'Grundstück'
        : 'Wohnung';
  // Badge oben rechts: Objekt-Typ (Wohnung/Haus/Grundstück) statt Deal-Typ — „Kauf" ist bei
  // Kauf-only-Konten (J&L) redundant, Wohnung/Haus ist informativer. Büro als Sonderfall.
  const typBadge = object.assignedProfile === 'B_buero' ? 'Büro' : objTyp;
  const ort = object.address ?? object.latestSnapshot?.locationText ?? '—';

  // Farbliche Bewertung der Kategorien aus dem Breakdown-Score
  const bd = object.score?.breakdown ?? [];
  const scoreOf = (key: string): number | null => {
    const e = bd.find((b) => b.key === key);
    if (!e || e.applicable === false) return null;
    return e.score;
  };
  // „Nur Eckdaten": reif (KI gelaufen), aber im Digest standen keine Detailangaben → alle
  // KI-Detailfelder null. Kein Fehler — das Inserat gibt in der Mail schlicht nichts her.
  const DETAIL_FIELDS = ['zustand', 'energieklasse', 'befristung', 'baujahr', 'außenbereich', 'stockwerk', 'lift_vorhanden', 'ablöse_euro'];
  const hasLlmDetail = DETAIL_FIELDS.some((f) => {
    const v = object.extracted?.[f];
    return v != null && v !== 'null';
  });
  const nurEckdaten = (object.score?.isReif ?? false) && !hasLlmDetail;

  const außenbereich = object.extracted?.['außenbereich'];
  const energieklasse = object.extracted?.['energieklasse'];
  const befristung = object.extracted?.['befristung'];
  const redFlagsRaw = object.extracted?.['red_flags'];
  let redFlags: string[] = [];
  if (redFlagsRaw && redFlagsRaw !== 'null' && redFlagsRaw !== '[]') {
    try {
      redFlags = JSON.parse(redFlagsRaw);
    } catch {
      // ignore
    }
  }
  const zusammenfassung = object.extracted?.['zusammenfassung'];

  // Lange Texte auf dem Handy begrenzen (voller Text auf der Detailseite per Tap).
  const clamp = (lines: number): CSSProperties => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  });

  return (
    <div style={{ position: 'relative' }}>
      <div ref={rootRef} {...handlers} style={style}>
        <Card
          padding="lg"
          style={{ position: 'relative', overflow: 'hidden', opacity: killed ? 0.6 : 1 }}
        >
          {isPullRight && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(135deg, rgba(16,185,129,${dragRatio * 0.18}) 0%, transparent 80%)`,
                pointerEvents: 'none',
                zIndex: 10,
              }}
            />
          )}
          {isPullLeft && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(225deg, rgba(239,68,68,${dragRatio * 0.18}) 0%, transparent 80%)`,
                pointerEvents: 'none',
                zIndex: 10,
              }}
            />
          )}
          {dragRatio > 0.4 && isPullRight && (
            <div
              style={{
                position: 'absolute',
                top: 24,
                left: 24,
                transform: 'rotate(-20deg)',
                border: '3px solid #10b981',
                borderRadius: 8,
                padding: '6px 14px',
                color: '#10b981',
                fontWeight: 800,
                fontSize: '1.5rem',
                letterSpacing: '0.1em',
                opacity: dragRatio,
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              ★ HOT
            </div>
          )}
          {dragRatio > 0.4 && isPullLeft && (
            <div
              style={{
                position: 'absolute',
                top: 24,
                right: 24,
                transform: 'rotate(20deg)',
                border: '3px solid #ef4444',
                borderRadius: 8,
                padding: '6px 14px',
                color: '#ef4444',
                fontWeight: 800,
                fontSize: '1.5rem',
                letterSpacing: '0.1em',
                opacity: dragRatio,
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              ✗ PASS
            </div>
          )}
          {upRatio > 0.4 && isPullUp && (
            <div
              style={{
                position: 'absolute',
                top: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                border: '3px solid #2563eb',
                borderRadius: 8,
                padding: '6px 14px',
                color: '#2563eb',
                fontWeight: 800,
                fontSize: '1.3rem',
                letterSpacing: '0.08em',
                opacity: upRatio,
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              🔗 INSERAT
            </div>
          )}

          <Stack gap="sm">
            {/* Medien-Kopf: Bild + Standort-Karte nebeneinander. Nur Bild → Bild allein,
                nur Koordinaten → Karte über volle Breite. Score-Overlay links, Profil rechts. */}
            {(hasImage || hasCoords) && (
              <div
                style={{
                  position: 'relative',
                  margin: '-20px -20px 0',
                  overflow: 'hidden',
                  lineHeight: 0,
                  background: '#e5e7eb',
                  display: 'flex',
                }}
              >
                {hasImage && (
                  <div style={{ flex: hasCoords ? '1 1 50%' : '1 1 100%', minWidth: 0, overflow: 'hidden' }}>
                    <img
                      src={imageUrl ?? undefined}
                      alt={objTyp}
                      loading="lazy"
                      draggable={false}
                      onError={() => setImgError(true)}
                      style={{ display: 'block', width: '100%', height: MEDIA_H, objectFit: 'cover' }}
                    />
                  </div>
                )}
                {hasCoords && (
                  <div style={{ flex: hasImage ? '1 1 50%' : '1 1 100%', minWidth: 0 }}>
                    <MiniMap object={object} height={MEDIA_H} />
                  </div>
                )}
                {/* Score oben links, Profil oben rechts — über dem gesamten Medienkopf. */}
                <div
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    borderRadius: '50%',
                    background: '#fff',
                    padding: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
                  }}
                >
                  <ScoreBadge score={object.score?.effectiveTotal} size="md" killed={killed} />
                </div>
                <Badge
                  color={profileBadgeColor}
                  variant="filled"
                  size="md"
                  tt="none"
                  style={{ position: 'absolute', top: 12, right: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
                >
                  {typBadge}
                </Badge>
              </div>
            )}

            {object.stale && (
              <Badge color="red" variant="filled" size="lg" radius="sm" fullWidth tt="none" style={{ fontWeight: 700 }}>
                ⚠ Älteres Inserat · {Math.round(object.ageDays)} Tage alt
              </Badge>
            )}

            {/* Weder Bild noch Karte: kompakte Zeile mit Score + Profil. */}
            {!hasImage && !hasCoords && (
              <Group justify="space-between" align="center" wrap="nowrap">
                <ScoreBadge score={object.score?.effectiveTotal} size="md" killed={killed} />
                <Badge color={profileBadgeColor} variant="light" size="md" tt="none">
                  {typBadge}
                </Badge>
              </Group>
            )}

            {/* Überschrift: Haus/Wohnung in <Ort> */}
            <Text fw={800} size="lg" style={{ letterSpacing: '-0.02em', wordBreak: 'break-word', ...clamp(2) }}>
              {objTyp} in {ort}
            </Text>

            {/* Kategorien mit Farbe je nach Score */}
            <Group gap="sm" wrap="wrap">
              <FactTile
                label={object.objektTyp === 'grundstueck' ? 'Grundstück' : 'Fläche'}
                value={fmtArea(object.latestSnapshot?.areaSqm)}
                tone={scoreTone(scoreOf(object.objektTyp === 'grundstueck' ? 'grundstücksfläche' : 'wohnfläche'))}
              />
              <FactTile
                label={isKauf ? 'Kaufpreis' : 'Miete'}
                value={fmtPrice(object.latestSnapshot?.price)}
                tone={scoreTone(scoreOf(isKauf ? 'kaufpreis' : 'preis'))}
              />
              {/* Zimmer nur bei Wohnung/Haus — bei Grundstück sinnlos. */}
              {object.objektTyp !== 'grundstueck' && (
                <FactTile
                  label="Zimmer"
                  value={object.latestSnapshot?.rooms != null ? String(object.latestSnapshot.rooms) : '—'}
                  tone={scoreTone(scoreOf('zimmer'))}
                />
              )}
              {/* Ein Feld pro Pendelziel (Eltern/Horvath/Schwester) — kürzere Zeit (Auto vs.
                  ÖV) mit Modus-Icon, Hintergrund nach Fahrzeit eingefärbt. */}
              {object.commutes.map((c) => {
                const { mins, icon } = bestCommute(c);
                const short = c.displayName.replace(/\s*\(.*\)$/, '');
                return (
                  <FactTile
                    key={c.target}
                    label={short}
                    value={mins !== null ? `${icon} ${Math.round(mins)} min` : '—'}
                    tone={commuteToneByMin(mins)}
                  />
                );
              })}
            </Group>

            {nurEckdaten && (
              <Text size="xs" c="dimmed" fs="italic">
                ℹ Nur Eckdaten — im Inserat stehen keine KI-auswertbaren Detailangaben.
              </Text>
            )}

            {/* Text aus dem Inserat + KI-Zusammenfassung (gekürzt — voll auf der Detailseite) */}
            {object.listing?.title && (
              <Text size="sm" fw={600} style={{ wordBreak: 'break-word', ...clamp(2) }}>
                {object.listing.title}
              </Text>
            )}
            {zusammenfassung && zusammenfassung !== 'null' && (
              <Text
                size="sm"
                c="dimmed"
                style={{ fontStyle: 'italic', borderLeft: '2px solid rgba(124,58,237,0.3)', paddingLeft: 10, ...clamp(3) }}
              >
                {zusammenfassung}
              </Text>
            )}

            {(außenbereich || energieklasse || befristung) && (
              <Group gap={4} wrap="wrap">
                {außenbereich && außenbereich !== 'null' && (
                  <Badge variant="light" color="green" tt="none">
                    🌳 {außenbereich}
                  </Badge>
                )}
                {energieklasse && energieklasse !== 'null' && (
                  <Badge variant="light" color="teal" tt="none">
                    ⚡ Klasse {energieklasse}
                  </Badge>
                )}
                {befristung && befristung !== 'null' && (
                  <Badge variant="light" color={befristung === 'unbefristet' ? 'green' : 'orange'} tt="none">
                    {befristung === 'unbefristet' ? '✓' : '⏱'} {befristung}
                  </Badge>
                )}
              </Group>
            )}

            {redFlags.length > 0 && (
              <Stack gap={2}>
                {redFlags.map((f) => (
                  <Text key={f} size="xs" c="red.7">
                    ⚠ {f}
                  </Text>
                ))}
              </Stack>
            )}

            {killed && object.score?.killedBy && (
              <Text size="xs" c="red.7" style={{ fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-word' }}>
                ✕ {object.score.killedBy}
              </Text>
            )}

            {/* Wann gefunden — rot wenn älter */}
            {object.listing?.firstSeenAt && (
              <Text size="xs" fw={object.stale ? 700 : 400} c={object.stale ? 'red.7' : 'dimmed'}>
                📅 {fmtFound(object.listing.firstSeenAt)}
                {object.stale ? ' · älter' : ''}
              </Text>
            )}

            {/* Links: Merken · Inserat · Details · Maps */}
            <Group gap="md" mt="xs" wrap="wrap" align="center">
              <FavoriteHeart objectId={object.id} favorited={object.favorited} variant="label" />
              {listingUrl && (
                <Anchor href={listingUrl} target="_blank" rel="noreferrer" size="sm" fw={500}>
                  🌐 Inserat
                </Anchor>
              )}
              <Anchor component={Link} to={`/objects/${object.id}`} size="sm" fw={500}>
                📊 Details
              </Anchor>
              <Anchor href={buildMapsUrl(object)} target="_blank" rel="noreferrer" size="sm" fw={500}>
                🗺 Maps
              </Anchor>
            </Group>
          </Stack>
        </Card>
      </div>
    </div>
  );
}

function FactTile({ label, value, tone }: { label: string; value: string; tone: Tone }): JSX.Element {
  const bg = tone === 'good' ? '#e7f9ee' : tone === 'bad' ? '#fdecec' : 'var(--mantine-color-gray-0)';
  const fg = tone === 'good' ? '#166534' : tone === 'bad' ? '#991b1b' : undefined;
  return (
    <Stack
      gap={0}
      style={{ background: bg, borderRadius: 10, padding: '6px 12px', minWidth: 78 }}
    >
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.05em', fontSize: '0.62rem' }}>
        {label}
      </Text>
      <Text fw={800} size="md" c={fg} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
    </Stack>
  );
}
